import { Group, Burger, Drawer, Anchor, Box } from '@mantine/core';
import { BrandWordmark } from '../UI/BrandWordmark';
import { useDisclosure } from '@mantine/hooks';
import { Link } from 'react-router-dom';
import { AuthButton } from '../AuthButton';
import { DesktopNav } from '../Navigation/DesktopNav';
import { MobileNav } from '../Navigation/MobileNav';
import { ReleaseNotificationBell } from '../ReleaseNotifications/ReleaseNotificationBell';
import { useAppUser } from '../../hooks/useAppUser';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useSubscription } from '../../hooks/useSubscription';
import styles from './AppHeader.module.css';

export const AppHeader = () => {
  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] = useDisclosure(false);
  // Note: unlike the profile dropdown (see AuthButton's navigateFromMenu),
  // drawer nav-item taps navigate immediately. Sequencing close-then-navigate
  // did not keep the open drawer out of Chrome's predictive-back screenshot
  // here, and the added close-before-route-change pause was itself jarring —
  // so the brief drawer flash on swipe-back is an accepted browser artifact.
  const { user, loading: userLoading } = useAppUser();
  const { hasCompletedOnboarding } = useOnboarding();
  const { hasAccess } = useSubscription();

  // Show navigation only if user is logged in AND has completed onboarding AND has subscription access
  const showNavigation = user && hasCompletedOnboarding === true && hasAccess;
  // Don't show "About" link while we're still loading user state
  const showAboutLink = !userLoading && !user;

  return (
    <>
      <Group h="100%" px="md" justify="space-between" wrap="nowrap">
        <Group wrap="nowrap">
          {showNavigation && (
            <Burger
              opened={drawerOpened}
              onClick={toggleDrawer}
              hiddenFrom="sm"
              size="sm"
            />
          )}
          <Link to="/" style={{ textDecoration: 'none' }}>
            <BrandWordmark markClassName={styles.hideLogo} />
          </Link>
        </Group>
        
        <Group gap="md" visibleFrom="sm">
          {showNavigation && <DesktopNav />}
          {showAboutLink && (
            <Anchor component={Link} to="/about" c="orange" fw={500} size="sm">
              About
            </Anchor>
          )}
          {showNavigation && <ReleaseNotificationBell />}
          <AuthButton />
        </Group>

        {/* Mobile auth button and about link */}
        <Group hiddenFrom="sm" gap="md" wrap="nowrap" style={{ flexShrink: 0 }}>
          {showAboutLink && (
            <Box className={styles.hideOnNarrow}>
              <Anchor component={Link} to="/about" c="orange" fw={500} size="sm">
                About
              </Anchor>
            </Box>
          )}
          {showNavigation && (
            <Box className={styles.hideBellOnNarrow}>
              <ReleaseNotificationBell />
            </Box>
          )}
          <Box className={styles.hideOnVeryNarrow}>
            <AuthButton />
          </Box>
          {/* Below 354px a compact button replaces the standard one so
              sign-in/account access never disappears */}
          <Box className={styles.compactAuthOnly}>
            <AuthButton size="xs" />
          </Box>
        </Group>
      </Group>

      {/* Mobile navigation drawer */}
      {showNavigation && (
        <Drawer
          opened={drawerOpened}
          onClose={closeDrawer}
          title="Navigation"
          hiddenFrom="sm"
          zIndex={1000000}
          size="sm"
          styles={{
            // Round the inward-facing edge, matching the app's other drawers.
            // The outer edge sits flush against the screen and stays square.
            content: {
              borderTopRightRadius: 'var(--mantine-radius-lg)',
              borderBottomRightRadius: 'var(--mantine-radius-lg)',
              overflow: 'hidden',
            },
          }}
        >
          <MobileNav onClose={closeDrawer} />
        </Drawer>
      )}
    </>
  );
};
